/**
 * =============================================================================
 * ANALYTICS DASHBOARD
 * =============================================================================
 * 
 * Main administrative dashboard for managing Widget and Map API keys.
 * Provides comprehensive analytics, key management, and system configuration.
 * 
 * Features:
 * - API key listing with search and filtering
 * - Status statistics (approved, pending, suspended)
 * - Product type filtering (Widget, Map, Both)
 * - Approve/Reject/Suspend/Delete key actions
 * - Detailed key information modal
 * - Email notification settings
 * - Iframe embedding support for external admin access
 * - Responsive design for mobile/desktop
 * 
 * Access Modes:
 * - Direct: /admin (requires password authentication)
 * - Embedded: /admin?embedded=true&apiKey=XXX (requires valid admin API key)
 * 
 * Authentication:
 * - Uses AuthContext for session management
 * - Supports both password and API key authentication
 * 
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AnalyticsDashboard.css';
import { useAuth } from '../contexts/AuthContext';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * AnalyticsDashboard Component
 * 
 * Main admin dashboard for API key management and analytics.
 * Supports both direct access (password) and embedded access (API key).
 */
const AnalyticsDashboard = () => {
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [filteredKeys, setFilteredKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and search state
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [statsView, setStatsView] = useState('all'); // 'all', 'widget', 'map'
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Suspend modal state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendModalData, setSuspendModalData] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedKeyDetail, setSelectedKeyDetail] = useState(null);
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);

  // Email settings modal state
  const [showEmailSettingsModal, setShowEmailSettingsModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailRecipientsLoading, setEmailRecipientsLoading] = useState(false);
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [newEmailName, setNewEmailName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Iframe/embedded authentication state
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isEmbeddedAuthenticated, setIsEmbeddedAuthenticated] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const { isAuthenticated, login, logout, loginLoading, getToken } = useAuth();
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [password, setPassword] = useState('');

  // ===== SMART BACKEND URL DETECTION =====
  const getBackendUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
      return process.env.REACT_APP_API_URL || 'http://localhost:5005';
    } else {
      return process.env.REACT_APP_BACKEND_URL || 'https://lgbt-widget-backend.onrender.com';
    }
  };

  // Check iframe mode and verify API key
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const embedded = urlParams.get('embedded') === 'true';
    const embeddedApiKey = urlParams.get('apiKey') || '';
    
    setIsEmbedded(embedded);
    setApiKey(embeddedApiKey);
    
    if (embedded && embeddedApiKey) {
      const backendUrl = getBackendUrl();
      console.log('Verifying admin API key with backend:', backendUrl);
      
      fetch(`${backendUrl}/api/admin/verify-iframe/${embeddedApiKey}`)
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            setIsEmbeddedAuthenticated(true);
            setAuthLoading(false);
          } else {
            setLoginError(data.message || 'Invalid admin API key. Access denied.');
            setAuthLoading(false);
          }
        })
        .catch(err => {
          console.error('Verify error:', err);
          setLoginError('Failed to verify admin access.');
          setAuthLoading(false);
        });
    } else if (embedded && !embeddedApiKey) {
      setLoginError('Admin API key is required for embedded access.');
      setAuthLoading(false);
    } else {
      setAuthLoading(false);
    }
  }, []);

  // Handle password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    const result = await login(password);
    
    if (result.success) {
      setPassword('');
    } else {
      setLoginError(result.error || 'Incorrect password. Please try again.');
      setPassword('');
    }
  };

  // Helper function to render product type badge
  const renderProductTypeBadge = (productType) => {
    const badges = {
      widget: { label: 'Widget', class: 'product-widget' },
      map: { label: 'Map', class: 'product-map' },
      both: { label: 'Both', class: 'product-both' }
    };
    
    const badge = badges[productType] || badges.both;
    
    return (
      <span className={`product-type-badge ${badge.class}`}>
        <span className="product-badge-dot"></span>
        <span className="product-label">{badge.label}</span>
      </span>
    );
  };

  // Fetch API keys
  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      console.log('Fetching API keys from:', backendUrl);
      
      const url = filterStatus === 'all' 
        ? `${backendUrl}/api/admin/keys`
        : `${backendUrl}/api/admin/keys?status=${filterStatus}`;
      
      const headers = {};
      if (isEmbedded && apiKey) {
        headers['X-Admin-Api-Key'] = apiKey;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, {
        headers,
        credentials: isEmbedded ? 'omit' : 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setApiKeys(data.keys);
        setFilteredKeys(data.keys);
      } else {
        throw new Error('Failed to fetch keys');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, isEmbedded, apiKey, getToken]);

  useEffect(() => {
    if ((isAuthenticated && !isEmbedded) || (isEmbedded && isEmbeddedAuthenticated)) {
      fetchKeys();
    }
  }, [fetchKeys, isAuthenticated, isEmbedded, isEmbeddedAuthenticated]);

  // Manual refresh handler with visual feedback
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchKeys();
    // Keep the spinner visible briefly so users see feedback even on fast connections
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Search and filter functionality - includes contact_name
  useEffect(() => {
    let filtered = apiKeys;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(key => 
        key.organization_name.toLowerCase().includes(query) ||
        key.email.toLowerCase().includes(query) ||
        key.api_key.toLowerCase().includes(query) ||
        (key.website_url && key.website_url.toLowerCase().includes(query)) ||
        (key.contact_name && key.contact_name.toLowerCase().includes(query))
      );
    }

    if (filterProduct !== 'all') {
      if (filterProduct === 'widget') {
        filtered = filtered.filter(key => key.product_type === 'widget' || key.product_type === 'both');
      } else if (filterProduct === 'map') {
        filtered = filtered.filter(key => key.product_type === 'map' || key.product_type === 'both');
      } else {
        filtered = filtered.filter(key => key.product_type === filterProduct);
      }
    }

    setFilteredKeys(filtered);
  }, [searchQuery, filterProduct, apiKeys]);

  // Calculate stats based on selected view
  const getStats = () => {
    let keysToCount = apiKeys;
    
    if (statsView === 'widget') {
      keysToCount = apiKeys.filter(k => k.product_type === 'widget' || k.product_type === 'both');
    } else if (statsView === 'map') {
      keysToCount = apiKeys.filter(k => k.product_type === 'map' || k.product_type === 'both');
    }

    return {
      total: keysToCount.length,
      pending: keysToCount.filter(k => k.status === 'pending').length,
      approved: keysToCount.filter(k => k.status === 'approved').length,
      rejected: keysToCount.filter(k => k.status === 'rejected').length,
      suspended: keysToCount.filter(k => k.status === 'suspended').length
    };
  };

  const stats = getStats();

  // Show confirmation modal
  const showConfirmation = (config) => {
    setModalConfig(config);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setModalConfig(null);
  };

  // Handle modal confirmation
  const handleModalConfirm = () => {
    if (modalConfig && modalConfig.onConfirm) {
      modalConfig.onConfirm();
    }
    closeModal();
  };

  // ===== DETAIL MODAL HANDLERS =====
  const openDetailModal = (key) => {
    setSelectedKeyDetail(key);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedKeyDetail(null);
    setApiKeyRevealed(false);
  };

  // ===== EMAIL SETTINGS HANDLERS =====
  const fetchEmailRecipients = async () => {
    setEmailRecipientsLoading(true);
    setEmailError('');
    try {
      const backendUrl = getBackendUrl();
      const headers = {};
      if (isEmbedded && apiKey) {
        headers['X-Admin-Api-Key'] = apiKey;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/email-recipients`, {
        headers,
        credentials: isEmbedded ? 'omit' : 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailRecipients(data.recipients);
      } else {
        setEmailError(data.message || 'Failed to fetch email recipients');
      }
    } catch (err) {
      console.error('Fetch email recipients error:', err);
      setEmailError('Failed to load email recipients');
    } finally {
      setEmailRecipientsLoading(false);
    }
  };

  const openEmailSettingsModal = () => {
    setShowEmailSettingsModal(true);
    setNewEmailAddress('');
    setNewEmailName('');
    setEmailError('');
    setEmailSuccess('');
    fetchEmailRecipients();
  };

  const closeEmailSettingsModal = () => {
    setShowEmailSettingsModal(false);
    setNewEmailAddress('');
    setNewEmailName('');
    setEmailError('');
    setEmailSuccess('');
  };

  const addEmailRecipient = async () => {
    if (!newEmailAddress.trim()) {
      setEmailError('Email address is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmailAddress)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setEmailSuccess('');
    setEmailRecipientsLoading(true);

    try {
      const backendUrl = getBackendUrl();
      const headers = { 'Content-Type': 'application/json' };
      if (isEmbedded && apiKey) {
        headers['X-Admin-Api-Key'] = apiKey;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/email-recipients`, {
        method: 'POST',
        headers,
        credentials: isEmbedded ? 'omit' : 'include',
        body: JSON.stringify({
          email: newEmailAddress.trim(),
          name: newEmailName.trim() || null,
          notificationType: 'approval'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailSuccess(`${newEmailAddress} added successfully!`);
        setNewEmailAddress('');
        setNewEmailName('');
        fetchEmailRecipients();
      } else {
        setEmailError(data.message || 'Failed to add email recipient');
      }
    } catch (err) {
      console.error('Add email recipient error:', err);
      setEmailError('Failed to add email recipient');
    } finally {
      setEmailRecipientsLoading(false);
    }
  };

  const toggleEmailRecipientActive = async (recipient) => {
    setEmailError('');
    setEmailSuccess('');

    try {
      const backendUrl = getBackendUrl();
      const headers = { 'Content-Type': 'application/json' };
      if (isEmbedded && apiKey) {
        headers['X-Admin-Api-Key'] = apiKey;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/email-recipients/${recipient.id}`, {
        method: 'PUT',
        headers,
        credentials: isEmbedded ? 'omit' : 'include',
        body: JSON.stringify({
          isActive: !recipient.is_active
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailSuccess(`${recipient.email} ${!recipient.is_active ? 'activated' : 'deactivated'}!`);
        fetchEmailRecipients();
      } else {
        setEmailError(data.message || 'Failed to update recipient');
      }
    } catch (err) {
      console.error('Toggle email recipient error:', err);
      setEmailError('Failed to update recipient');
    }
  };

  const deleteEmailRecipient = async (recipient) => {
    if (!window.confirm(`Are you sure you want to remove ${recipient.email} from the notification list?`)) {
      return;
    }

    setEmailError('');
    setEmailSuccess('');

    try {
      const backendUrl = getBackendUrl();
      const headers = {};
      if (isEmbedded && apiKey) {
        headers['X-Admin-Api-Key'] = apiKey;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/email-recipients/${recipient.id}`, {
        method: 'DELETE',
        headers,
        credentials: isEmbedded ? 'omit' : 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailSuccess(`${recipient.email} removed successfully!`);
        if (data.warning) {
          setEmailError(data.warning);
        }
        fetchEmailRecipients();
      } else {
        setEmailError(data.message || 'Failed to delete recipient');
      }
    } catch (err) {
      console.error('Delete email recipient error:', err);
      setEmailError('Failed to delete recipient');
    }
  };

  // Download all emails - includes contact_name
  const downloadEmails = () => {
    showConfirmation({
      icon: '✓',
      iconType: 'default',
      title: 'Download Email List',
      message: 'This will download a CSV file containing organization names, contact names, email addresses, and creation dates for all API keys.',
      details: (
        <>
          <p><strong>Total Records:</strong> {apiKeys.length}</p>
          <p><strong>Format:</strong> CSV (Excel compatible)</p>
          <p><strong>Columns:</strong> Organization, Contact, Email, Product Type, Display Mode, Website, Date</p>
        </>
      ),
      confirmText: 'Download',
      confirmType: 'default',
      onConfirm: () => {
        const headers = 'Organization Name,Contact Name,Email Address,Product Type,Display Mode,Website URL,Date Created\n';
        const csvContent = apiKeys.map(key => {
          const productType = key.product_type === 'widget' ? 'Widget Only' : 
                             key.product_type === 'map' ? 'Map Only' : 'Both';
          const displayMode = key.product_type === 'map' 
                             ? 'Iframe Embed' 
                             : key.display_mode === 'static' 
                               ? 'Iframe Embed' 
                               : 'Floating Widget';
          const websiteUrl = key.website_url || 'N/A';
          const contactName = key.contact_name || 'N/A';
          return `"${key.organization_name}","${contactName}","${key.email}","${productType}","${displayMode}","${websiteUrl}","${new Date(key.created_at).toLocaleDateString()}"`;
        }).join('\n');
        
        const blob = new Blob([headers + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `api-keys-emails-${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  // Action handlers
  const handleApprove = (key) => {
    showConfirmation({
      icon: '✓',
      iconType: 'default',
      title: 'Approve API Key',
      message: `Approve API key for ${key.organization_name}?`,
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          {key.contact_name && <p><strong>Contact:</strong> {key.contact_name}</p>}
          <p><strong>Email:</strong> {key.email}</p>
          <p><strong>Product:</strong> {key.product_type === 'widget' ? 'Widget Only' : 
                                         key.product_type === 'map' ? 'Map Only' : 'Both'}</p>
          <p><strong>Display:</strong> {key.product_type === 'map' 
            ? 'Iframe Embed' 
            : key.display_mode === 'static' 
              ? 'Iframe Embed' 
              : 'Floating Widget'}</p>
        </>
      ),
      confirmText: 'Approve',
      confirmType: 'default',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const backendUrl = getBackendUrl();
          const headers = { 'Content-Type': 'application/json' };
          if (isEmbedded && apiKey) {
            headers['X-Admin-Api-Key'] = apiKey;
          } else {
            const token = getToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${backendUrl}/api/admin/keys/${key.api_key}/approve`, {
            method: 'PUT',
            headers,
            credentials: isEmbedded ? 'omit' : 'include',
            body: JSON.stringify({ 
              approvedBy: isEmbedded ? 'Embedded Admin' : 'admin' 
            })
          });
          
          if (response.ok) {
            await fetchKeys();
          } else {
            throw new Error('Failed to approve key');
          }
        } catch (err) {
          console.error('Approve error:', err);
          alert('Failed to approve API key');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (key) => {
    const notes = prompt('Rejection reason (optional):');
    
    showConfirmation({
      icon: '✕',
      iconType: 'danger',
      title: 'Reject API Key',
      message: `Reject API key request from ${key.organization_name}?`,
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          {key.contact_name && <p><strong>Contact:</strong> {key.contact_name}</p>}
          <p><strong>Email:</strong> {key.email}</p>
          {notes && <p><strong>Reason:</strong> {notes}</p>}
        </>
      ),
      confirmText: 'Reject',
      confirmType: 'danger',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const backendUrl = getBackendUrl();
          const headers = { 'Content-Type': 'application/json' };
          if (isEmbedded && apiKey) {
            headers['X-Admin-Api-Key'] = apiKey;
          } else {
            const token = getToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${backendUrl}/api/admin/keys/${key.api_key}/reject`, {
            method: 'PUT',
            headers,
            credentials: isEmbedded ? 'omit' : 'include',
            body: JSON.stringify({ notes })
          });
          
          if (response.ok) {
            await fetchKeys();
          } else {
            throw new Error('Failed to reject key');
          }
        } catch (err) {
          console.error('Reject error:', err);
          alert('Failed to reject API key');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleSuspend = (key) => {
    setSuspendModalData(key);
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  const confirmSuspend = async () => {
    if (!suspendModalData) return;
    
    setActionLoading(true);
    setShowSuspendModal(false);
    
    try {
      const backendUrl = getBackendUrl();
      const headers = { 'Content-Type': 'application/json' };
      if (isEmbedded && apiKey) {
        headers['X-Admin-Api-Key'] = apiKey;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/keys/${suspendModalData.api_key}/suspend`, {
        method: 'PUT',
        headers,
        credentials: isEmbedded ? 'omit' : 'include',
        body: JSON.stringify({ notes: suspendReason || null })
      });
      
      if (response.ok) {
        await fetchKeys();
      } else {
        throw new Error('Failed to suspend key');
      }
    } catch (err) {
      console.error('Suspend error:', err);
      alert('Failed to suspend API key');
    } finally {
      setActionLoading(false);
      setSuspendModalData(null);
      setSuspendReason('');
    }
  };

  const handleUnsuspend = (key) => {
    showConfirmation({
      icon: '✓',
      iconType: 'success',
      title: 'Reactivate API Key',
      message: `Reactivate API key for ${key.organization_name}?`,
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          {key.contact_name && <p><strong>Contact:</strong> {key.contact_name}</p>}
          <p><strong>Email:</strong> {key.email}</p>
          {key.notes && <p><strong>Suspension Reason:</strong> {key.notes}</p>}
          <p style={{ color: '#5CB85C', marginTop: '1rem' }}>
            ✓ This will restore widget/map access.
          </p>
        </>
      ),
      confirmText: 'Reactivate',
      confirmType: 'success',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const backendUrl = getBackendUrl();
          const headers = { 'Content-Type': 'application/json' };
          if (isEmbedded && apiKey) {
            headers['X-Admin-Api-Key'] = apiKey;
          } else {
            const token = getToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${backendUrl}/api/admin/keys/${key.api_key}/approve`, {
            method: 'PUT',
            headers,
            credentials: isEmbedded ? 'omit' : 'include',
            body: JSON.stringify({ 
              approvedBy: isEmbedded ? 'Embedded Admin' : 'admin' 
            })
          });
          
          if (response.ok) {
            await fetchKeys();
          } else {
            throw new Error('Failed to reactivate key');
          }
        } catch (err) {
          console.error('Reactivate error:', err);
          alert('Failed to reactivate API key');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleDelete = (key) => {
    showConfirmation({
      icon: '⚠',
      iconType: 'danger',
      title: 'Delete API Key',
      message: `Permanently delete API key for ${key.organization_name}?`,
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          {key.contact_name && <p><strong>Contact:</strong> {key.contact_name}</p>}
          <p><strong>Email:</strong> {key.email}</p>
          <p style={{ color: '#ff6b6b', marginTop: '1rem' }}>
            ⚠ This will permanently delete all associated data.
          </p>
        </>
      ),
      confirmText: 'Delete',
      confirmType: 'danger',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const backendUrl = getBackendUrl();
          const headers = {};
          if (isEmbedded && apiKey) {
            headers['X-Admin-Api-Key'] = apiKey;
          } else {
            const token = getToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${backendUrl}/api/admin/keys/${key.api_key}`, {
            method: 'DELETE',
            headers,
            credentials: isEmbedded ? 'omit' : 'include'
          });
          
          if (response.ok) {
            await fetchKeys();
          } else {
            throw new Error('Failed to delete key');
          }
        } catch (err) {
          console.error('Delete error:', err);
          alert('Failed to delete API key');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // ===== AUTHENTICATION SCREENS =====
  if (authLoading) {
    return (
      <div className="analytics-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <h2>Loading...</h2>
          <p style={{ color: '#af8bef' }}>Verifying admin access</p>
        </div>
      </div>
    );
  }

  if (isEmbedded && !isEmbeddedAuthenticated) {
    return (
      <div className="analytics-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: 'white', maxWidth: '500px', padding: '2rem', background: 'rgba(255,255,255,0.05)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ color: '#ff6b6b' }}>🔒 Access Denied</h2>
          <p style={{ color: '#af8bef', margin: '1rem 0' }}>{loginError || 'Invalid admin API key'}</p>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>Please contact the administrator for a valid embedded admin API key.</p>
        </div>
      </div>
    );
  }

  if (!isEmbedded && !isAuthenticated) {
    return (
      <div className="analytics-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(30px)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '20px', padding: '2.5rem', maxWidth: '400px', width: '90%' }}>
          <h2 style={{ color: 'white', textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.8rem', fontWeight: '700' }}>🔐 Admin Login</h2>
          
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {loginError && (
              <div style={{ background: 'rgba(220, 53, 69, 0.2)', border: '1px solid rgba(220, 53, 69, 0.4)', borderRadius: '8px', padding: '0.75rem 1rem', color: '#ff6b7a', fontSize: '0.9rem', textAlign: 'center' }}>
                {loginError}
              </div>
            )}

            <div>
              <label htmlFor="password" style={{ color: '#af8bef', display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>Password:</label>
              <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" required autoFocus style={{ width: '100%', padding: '0.875rem 1rem', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', color: 'white', fontSize: '1rem', fontFamily: 'inherit' }} />
            </div>

            <button 
              type="submit" 
              disabled={loginLoading}
              style={{ 
                background: loginLoading ? 'rgba(255,255,255,0.2)' : 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '1rem 2rem', 
                borderRadius: '8px', 
                fontSize: '1rem', 
                fontWeight: '600', 
                cursor: loginLoading ? 'not-allowed' : 'pointer', 
                transition: 'all 0.3s', 
                boxShadow: '0 4px 20px rgba(73, 25, 148, 0.3)',
                opacity: loginLoading ? 0.7 : 1
              }}
            >
              {loginLoading ? 'Logging in...' : 'Login'}
            </button>

            {!isEmbedded && (
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <a href="/" style={{ color: '#af8bef', textDecoration: 'none', fontSize: '0.9rem' }}>← Back to Home</a>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  const backendUrl = getBackendUrl();
  const isLocalBackend = backendUrl.includes('localhost');

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <p>Manage API keys and monitor usage</p>
          {isEmbedded && isEmbeddedAuthenticated && (
            <p style={{ color: '#5CB85C', marginTop: '0.25rem', fontSize: '0.8rem' }}>
              ✓ Embedded Mode{isLocalBackend && ' (Local)'}
            </p>
          )}
        </div>
        {!isEmbedded && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={openEmailSettingsModal}
              className="back-link"
              style={{ 
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                border: '1.5px solid rgba(255, 193, 7, 0.4)',
                cursor: 'pointer',
                color: '#ffc107',
                fontWeight: '600'
              }}
            >
              📧 Email
            </button>
            <a
              href="/admin/embed"
              className="back-link"
              style={{ 
                background: 'linear-gradient(135deg, rgba(92, 184, 92, 0.15) 0%, rgba(73, 25, 148, 0.15) 100%)',
                border: '1.5px solid rgba(92, 184, 92, 0.4)',
                textDecoration: 'none',
                color: '#7DCD7D',
                fontWeight: '600'
              }}
            >
              Embed
            </a>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="back-link"
              style={{ 
                background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.15) 0%, rgba(200, 35, 51, 0.15) 100%)',
                border: '1.5px solid rgba(220, 53, 69, 0.4)',
                cursor: 'pointer',
                color: '#ff6b7a',
                fontWeight: '600'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Admin Stats View Slider */}
      <div className="admin-stats-slider">
        <button 
          className={`admin-stats-btn ${statsView === 'all' ? 'active' : ''}`}
          onClick={() => { setStatsView('all'); setFilterProduct('all'); }}
        >
          <span className="admin-stats-label">All</span>
        </button>
        <button 
          className={`admin-stats-btn ${statsView === 'widget' ? 'active' : ''}`}
          onClick={() => { setStatsView('widget'); setFilterProduct('widget'); }}
        >
          <span className="admin-stats-label">Widget</span>
        </button>
        <button 
          className={`admin-stats-btn ${statsView === 'map' ? 'active' : ''}`}
          onClick={() => { setStatsView('map'); setFilterProduct('map'); }}
        >
          <span className="admin-stats-label">Map</span>
        </button>
        <div className="admin-stats-indicator" style={{ 
          transform: `translateX(${statsView === 'all' ? '0%' : statsView === 'widget' ? '100%' : '200%'})` 
        }}></div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon icon-total"></div>
          <div className="summary-info">
            <h3>{stats.total}</h3>
            <p>Total</p>
          </div>
        </div>
        <div className="summary-card pending">
          <div className="summary-icon icon-pending"></div>
          <div className="summary-info">
            <h3>{stats.pending}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className="summary-card approved">
          <div className="summary-icon icon-approved"></div>
          <div className="summary-info">
            <h3>{stats.approved}</h3>
            <p>Approved</p>
          </div>
        </div>
        <div className="summary-card rejected">
          <div className="summary-icon icon-rejected"></div>
          <div className="summary-info">
            <h3>{stats.rejected}</h3>
            <p>Rejected</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-bar">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="Search organization, contact, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="download-btn" onClick={downloadEmails}>
          Download
        </button>
      </div>

      {/* Filter Tabs with Refresh Button */}
      <div className="filter-row">
        <div className="filter-tabs">
          <button 
            className={filterStatus === 'all' ? 'active' : ''}
            onClick={() => setFilterStatus('all')}
          >
            All ({apiKeys.length})
          </button>
          <button 
            className={filterStatus === 'pending' ? 'active' : ''}
            onClick={() => setFilterStatus('pending')}
          >
            Pending ({apiKeys.filter(k => k.status === 'pending').length})
          </button>
          <button 
            className={filterStatus === 'approved' ? 'active' : ''}
            onClick={() => setFilterStatus('approved')}
          >
            Approved ({apiKeys.filter(k => k.status === 'approved').length})
          </button>
          <button 
            className={filterStatus === 'rejected' ? 'active' : ''}
            onClick={() => setFilterStatus('rejected')}
          >
            Rejected ({apiKeys.filter(k => k.status === 'rejected').length})
          </button>
        </div>
        <button
          className={`refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
          onClick={handleManualRefresh}
          disabled={isRefreshing || loading}
          title="Refresh API keys"
        >
          <span className="refresh-icon">↻</span>
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Product Filter Tabs */}
      <div className="filter-tabs" style={{ marginBottom: '0.75rem' }}>
        <button 
          className={filterProduct === 'all' ? 'active' : ''}
          onClick={() => { setFilterProduct('all'); setStatsView('all'); }}
        >
          All Products
        </button>
        <button 
          className={filterProduct === 'widget' ? 'active' : ''}
          onClick={() => { setFilterProduct('widget'); setStatsView('widget'); }}
        >
          Widget
        </button>
        <button 
          className={filterProduct === 'map' ? 'active' : ''}
          onClick={() => { setFilterProduct('map'); setStatsView('map'); }}
        >
          Map
        </button>
        <button 
          className={filterProduct === 'both' ? 'active' : ''}
          onClick={() => { setFilterProduct('both'); setStatsView('all'); }}
        >
          Both
        </button>
      </div>

      {/* COMPACT Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading API keys...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : filteredKeys.length === 0 ? (
          <div className="empty-state">No API keys found</div>
        ) : (
          <table className="keys-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Product</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Created</th>
                <th>Info</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredKeys.map(key => (
                <tr key={key.api_key}>
                  <td className="org-name" title={key.organization_name}>{key.organization_name}</td>
                  <td className="contact-name" title={key.contact_name || ''}>
                    {key.contact_name || <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td className="email" title={key.email}>{key.email}</td>
                  <td className="product-cell">
                    {renderProductTypeBadge(key.product_type || 'both')}
                  </td>
                  <td>
                    {key.product_type === 'widget' ? (
                      <span className="mode-badge">{key.widget_mode || 'adult'}</span>
                    ) : key.product_type === 'map' ? (
                      <span className="mode-badge">{key.map_mode || 'adult'}</span>
                    ) : (
                      <span className="mode-badge">{key.widget_mode || key.map_mode || 'adult'}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${key.status}`}>
                      {key.status}
                    </span>
                  </td>
                  <td className="date">
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <button 
                      className={`info-btn ${key.description ? 'has-comment' : ''}`}
                      onClick={() => openDetailModal(key)}
                      title={key.description ? 'View details & comment' : 'View details'}
                    >
                      {key.description ? '💬' : 'ℹ️'}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {key.status === 'pending' && (
                        <>
                          <button 
                            className="action-btn approve"
                            onClick={() => handleApprove(key)}
                            disabled={actionLoading}
                            title="Approve this API key"
                          >
                            Accept
                          </button>
                          <button 
                            className="action-btn reject"
                            onClick={() => handleReject(key)}
                            disabled={actionLoading}
                            title="Reject this API key"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {key.status === 'approved' && (
                        <button 
                          className="action-btn revoke"
                          onClick={() => handleSuspend(key)}
                          disabled={actionLoading}
                          title="Suspend this API key"
                        >
                          Suspend
                        </button>
                      )}
                      {key.status === 'suspended' && (
                        <button 
                          className="action-btn approve"
                          onClick={() => handleUnsuspend(key)}
                          disabled={actionLoading}
                          title="Reactivate this API key"
                        >
                          Activate
                        </button>
                      )}
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDelete(key)}
                        disabled={actionLoading}
                        title="Permanently delete this API key"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && modalConfig && (
        <div className="confirmation-modal-overlay" onClick={closeModal}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-icon ${modalConfig.iconType}`}>
              {modalConfig.icon}
            </div>
            <h3>{modalConfig.title}</h3>
            <p>{modalConfig.message}</p>
            {modalConfig.details && (
              <div className="modal-details">
                {modalConfig.details}
              </div>
            )}
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={closeModal}>
                Cancel
              </button>
              <button 
                className={`modal-btn confirm ${modalConfig.confirmType}`}
                onClick={handleModalConfirm}
              >
                {modalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="confirmation-modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon warning">⚠️</div>
            <h3>Logout Confirmation</h3>
            <p>Are you sure you want to logout?</p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setShowLogoutModal(false)}>
                Cancel
              </button>
              <button 
                className="modal-btn confirm warning"
                onClick={() => {
                  setShowLogoutModal(false);
                  logout();
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && suspendModalData && (
        <div className="confirmation-modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon warning">⚠️</div>
            <h3>Suspend API Key</h3>
            <p>Suspend API key for <strong>{suspendModalData.organization_name}</strong>?</p>
            
            <div className="modal-details">
              <p><strong>Organization:</strong> {suspendModalData.organization_name}</p>
              {suspendModalData.contact_name && <p><strong>Contact:</strong> {suspendModalData.contact_name}</p>}
              <p><strong>Email:</strong> {suspendModalData.email}</p>
              <p style={{ color: '#ff6b6b', marginTop: '0.5rem' }}>
                ⚠️ This will immediately revoke access.
              </p>
            </div>

            <div className="modal-input-group">
              <label htmlFor="suspend-reason">Suspension reason (optional):</label>
              <textarea
                id="suspend-reason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Enter reason for suspension..."
                rows="3"
                className="modal-textarea"
              />
            </div>
            
            <div className="modal-actions">
              <button 
                className="modal-btn cancel" 
                onClick={() => {
                  setShowSuspendModal(false);
                  setSuspendModalData(null);
                  setSuspendReason('');
                }}
              >
                Cancel
              </button>
              <button 
                className="modal-btn confirm warning"
                onClick={confirmSuspend}
              >
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== KEY DETAIL MODAL ===== */}
      {showDetailModal && selectedKeyDetail && (
        <div className="detail-modal-overlay" onClick={closeDetailModal}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h3>📋 API Key Details</h3>
              <button className="detail-modal-close" onClick={closeDetailModal}>×</button>
            </div>
            <div className="detail-modal-content">
              <div className="detail-row">
                <span className="detail-label">Organization</span>
                <span className="detail-value">{selectedKeyDetail.organization_name}</span>
              </div>
              
              {selectedKeyDetail.contact_name && (
                <div className="detail-row">
                  <span className="detail-label">Contact Name</span>
                  <span className="detail-value">{selectedKeyDetail.contact_name}</span>
                </div>
              )}
              
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value">{selectedKeyDetail.email}</span>
              </div>
              
              {selectedKeyDetail.website_url && (
                <div className="detail-row">
                  <span className="detail-label">Website</span>
                  <span className="detail-value">
                    <a 
                      href={selectedKeyDetail.website_url.startsWith('http') ? selectedKeyDetail.website_url : `https://${selectedKeyDetail.website_url}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#af8bef' }}
                    >
                      {selectedKeyDetail.website_url}
                    </a>
                  </span>
                </div>
              )}
              
              <div className="detail-row-inline">
                <div className="detail-row">
                  <span className="detail-label">Product Type</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedKeyDetail.product_type || 'both'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedKeyDetail.status}
                  </span>
                </div>
              </div>
              
              <div className="detail-row-inline">
                <div className="detail-row">
                  <span className="detail-label">Widget Mode</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedKeyDetail.widget_mode || 'adult'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Map Mode</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedKeyDetail.map_mode || 'adult'}
                  </span>
                </div>
              </div>
              
              <div className="detail-row-inline">
                <div className="detail-row">
                  <span className="detail-label">Color Theme</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedKeyDetail.color_theme || 'purple'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Display Mode</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {selectedKeyDetail.product_type === 'map' 
                      ? '📋 Iframe Embed'
                      : selectedKeyDetail.display_mode === 'static' 
                        ? '📋 Iframe Embed' 
                        : '💬 Floating Widget'}
                  </span>
                </div>
              </div>
              
              <div className="detail-row-inline">
                <div className="detail-row">
                  <span className="detail-label">Created</span>
                  <span className="detail-value">
                    {new Date(selectedKeyDetail.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">API Key</span>
                <span 
                  className={`detail-value api-key-field ${apiKeyRevealed ? 'revealed' : 'hidden'}`}
                  onClick={() => setApiKeyRevealed(!apiKeyRevealed)}
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '0.75rem', 
                    wordBreak: 'break-all',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  title={apiKeyRevealed ? 'Click to hide' : 'Click to reveal'}
                >
                  {apiKeyRevealed ? (
                    selectedKeyDetail.api_key
                  ) : (
                    <span style={{ color: '#af8bef', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>Click to reveal API key</span>
                    </span>
                  )}
                </span>
              </div>
              
              {selectedKeyDetail.description && (
                <div className="detail-row">
                  <span className="detail-label">About / Comment</span>
                  <span className="detail-value description">
                    {selectedKeyDetail.description}
                  </span>
                </div>
              )}
              
              {selectedKeyDetail.notes && (
                <div className="detail-row">
                  <span className="detail-label">Admin Notes</span>
                  <span className="detail-value description">
                    {selectedKeyDetail.notes}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== EMAIL SETTINGS MODAL ===== */}
      {showEmailSettingsModal && (
        <div className="confirmation-modal-overlay" onClick={closeEmailSettingsModal}>
          <div className="confirmation-modal email-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon" style={{ background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' }}>
              📧
            </div>
            <h3>Email Settings</h3>
            <p>Manage notification recipients</p>
            
            {emailError && (
              <div style={{ 
                background: 'rgba(220, 53, 69, 0.2)', 
                border: '1px solid rgba(220, 53, 69, 0.4)', 
                borderRadius: '8px', 
                padding: '0.5rem 0.75rem', 
                color: '#ff6b7a', 
                fontSize: '0.8rem',
                marginBottom: '0.75rem'
              }}>
                {emailError}
              </div>
            )}
            {emailSuccess && (
              <div style={{ 
                background: 'rgba(92, 184, 92, 0.2)', 
                border: '1px solid rgba(92, 184, 92, 0.4)', 
                borderRadius: '8px', 
                padding: '0.5rem 0.75rem', 
                color: '#7DCD7D', 
                fontSize: '0.8rem',
                marginBottom: '0.75rem'
              }}>
                {emailSuccess}
              </div>
            )}

            <div className="modal-details" style={{ marginBottom: '0.75rem' }}>
              <p style={{ marginBottom: '0.5rem', color: '#af8bef', fontWeight: '600', fontSize: '0.8rem' }}>Add New Recipient:</p>
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <input
                  type="email"
                  value={newEmailAddress}
                  onChange={(e) => setNewEmailAddress(e.target.value)}
                  placeholder="Email *"
                  style={{
                    flex: 2,
                    padding: '0.5rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '0.8rem'
                  }}
                />
                <input
                  type="text"
                  value={newEmailName}
                  onChange={(e) => setNewEmailName(e.target.value)}
                  placeholder="Name"
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '0.8rem'
                  }}
                />
              </div>
              <button
                onClick={addEmailRecipient}
                disabled={emailRecipientsLoading || !newEmailAddress.trim()}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: emailRecipientsLoading || !newEmailAddress.trim() 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  cursor: emailRecipientsLoading || !newEmailAddress.trim() ? 'not-allowed' : 'pointer',
                  opacity: emailRecipientsLoading || !newEmailAddress.trim() ? 0.6 : 1
                }}
              >
                {emailRecipientsLoading ? 'Adding...' : '+ Add'}
              </button>
            </div>

            <div className="modal-details" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <p style={{ marginBottom: '0.5rem', color: '#af8bef', fontWeight: '600', fontSize: '0.8rem' }}>
                Recipients ({emailRecipients.length}):
              </p>
              
              {emailRecipientsLoading && emailRecipients.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center', padding: '0.75rem', fontSize: '0.8rem' }}>Loading...</p>
              ) : emailRecipients.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center', padding: '0.75rem', fontSize: '0.8rem' }}>No recipients</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {emailRecipients.map((recipient) => (
                    <div 
                      key={recipient.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        background: recipient.is_active 
                          ? 'rgba(92, 184, 92, 0.1)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        border: `1px solid ${recipient.is_active 
                          ? 'rgba(92, 184, 92, 0.3)' 
                          : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '6px',
                        opacity: recipient.is_active ? 1 : 0.6
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ 
                          color: 'white', 
                          margin: 0, 
                          fontWeight: '500',
                          fontSize: '0.8rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {recipient.email}
                        </p>
                        {recipient.name && (
                          <p style={{ 
                            color: '#af8bef', 
                            margin: 0, 
                            fontSize: '0.7rem' 
                          }}>
                            {recipient.name}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                        <button
                          onClick={() => toggleEmailRecipientActive(recipient)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: recipient.is_active 
                              ? 'rgba(255, 193, 7, 0.2)' 
                              : 'rgba(92, 184, 92, 0.2)',
                            border: 'none',
                            borderRadius: '4px',
                            color: recipient.is_active ? '#ffc107' : '#7DCD7D',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: '600'
                          }}
                        >
                          {recipient.is_active ? '⏸' : '▶'}
                        </button>
                        <button
                          onClick={() => deleteEmailRecipient(recipient)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: 'rgba(220, 53, 69, 0.2)',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#ff6b7a',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: '600'
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: '1rem' }}>
              <button className="modal-btn cancel" onClick={closeEmailSettingsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;